import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box, Typography, TextField, Select, MenuItem, FormControl,
  InputLabel, Grid, Card, CardContent, Chip, CircularProgress, LinearProgress,
  IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  Button, Pagination, Checkbox, ListItemText, OutlinedInput, Alert, InputAdornment,
  Snackbar, Tooltip
} from '@mui/material';
import { Edit, Delete, ContentCopy, Search, Clear, Add, FilterAltOff } from '@mui/icons-material';
import api from '../api';
import Duplikate from './Duplikate';
import { useKonto } from '../konto';
import {
  Gericht, KATEGORIEN, SAISONEN, SUBKATEGORIEN,
  KATEGORIE_FARBEN, SUBKATEGORIE_FARBEN, SAISON_FARBEN, datumFormat,
} from '../konstanten';

const PRO_SEITE = 48;
const FILTER_SPEICHER = 'gerichte-filter';
const SORTIERUNGEN = [
  { wert: 'name', label: 'Name A–Z' },
  { wert: 'datum', label: 'Datum (neueste zuerst)' },
];

interface Filter {
  suche: string;
  kategorie: string;
  saison: string;
  subkategorie: string;
  sortierung: string;
}
const LEERE_FILTER: Filter = { suche: '', kategorie: '', saison: '', subkategorie: '', sortierung: 'name' };

// Filter bleiben beim Wechsel zwischen den Seiten erhalten (bis der Tab geschlossen wird)
function gespeicherteFilter(): Filter {
  try {
    return { ...LEERE_FILTER, ...JSON.parse(sessionStorage.getItem(FILTER_SPEICHER) || '{}') };
  } catch {
    return LEERE_FILTER;
  }
}

interface Formular {
  name: string;
  kategorie: string;
  subkategorie: string;
  saisonen: string[];
}

function FilterSelect({ label, value, optionen, onChange, alleLabel = 'Alle', minWidth = 150 }: {
  label: string; value: string; optionen: { wert: string; label: string }[] | string[];
  onChange: (v: string) => void; alleLabel?: string | null; minWidth?: number;
}) {
  const liste = (optionen as any[]).map(o => typeof o === 'string' ? { wert: o, label: o } : o);
  return (
    <FormControl size="small" sx={{ minWidth: { xs: 0, sm: minWidth }, flex: { xs: '1 1 calc(50% - 8px)', sm: '0 0 auto' } }}>
      <InputLabel>{label}</InputLabel>
      <Select value={value} label={label} onChange={e => onChange(e.target.value)}>
        {alleLabel !== null && <MenuItem value="">{alleLabel}</MenuItem>}
        {liste.map(o => <MenuItem key={o.wert} value={o.wert}>{o.label}</MenuItem>)}
      </Select>
    </FormControl>
  );
}

export default function Gerichte({ startSuche }: { startSuche?: string }) {
  const [filter, setFilter] = useState<Filter>(() =>
    startSuche !== undefined ? { ...LEERE_FILTER, suche: startSuche } : gespeicherteFilter());
  const [suche, setSuche] = useState(filter.suche);
  const [gerichte, setGerichte] = useState<Gericht[]>([]);
  const [laden, setLaden] = useState(true);
  const [ersteLadung, setErsteLadung] = useState(true);
  const [seite, setSeite] = useState(1);
  const [gesamt, setGesamt] = useState(0);
  const [fehler, setFehler] = useState('');
  const [meldung, setMeldung] = useState('');
  const [duplikateAnsicht, setDuplikateAnsicht] = useState(false);
  const { darfBearbeiten } = useKonto();

  // Bearbeiten/Anlegen: editId null = neues Gericht
  const [formularOffen, setFormularOffen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [formular, setFormular] = useState<Formular>({ name: '', kategorie: '', subkategorie: '', saisonen: [] });
  const [formFehler, setFormFehler] = useState('');
  const [loeschKandidat, setLoeschKandidat] = useState<Gericht | null>(null);
  const [beschaeftigt, setBeschaeftigt] = useState(false);
  const anfrage = useRef<AbortController | null>(null);
  const listeOben = useRef<HTMLDivElement | null>(null);

  const setze = (teil: Partial<Filter>) => setFilter(f => ({ ...f, ...teil }));

  useEffect(() => {
    sessionStorage.setItem(FILTER_SPEICHER, JSON.stringify(filter));
  }, [filter]);

  // Suchverzögerung 400ms
  useEffect(() => {
    const timer = setTimeout(() => setSuche(filter.suche.trim()), 400);
    return () => clearTimeout(timer);
  }, [filter.suche]);

  const ladeGerichte = useCallback(() => {
    anfrage.current?.abort();
    const controller = new AbortController();
    anfrage.current = controller;
    const params: Record<string, string> = {
      limit: String(PRO_SEITE), offset: String((seite - 1) * PRO_SEITE), sortierung: filter.sortierung,
    };
    if (filter.kategorie) params.kategorie = filter.kategorie;
    if (filter.saison) params.saison = filter.saison;
    if (filter.subkategorie) params.subkategorie = filter.subkategorie;
    if (suche) params.suche = suche;
    setLaden(true);
    setFehler('');
    api.get('/gerichte', { params, signal: controller.signal })
      .then(res => {
        if (controller.signal.aborted) return;
        const letzteSeite = Math.max(1, Math.ceil(res.data.gesamt / PRO_SEITE));
        if (seite > letzteSeite) { setSeite(letzteSeite); return; }
        setGerichte(res.data.gerichte);
        setGesamt(res.data.gesamt);
        setErsteLadung(false);
      })
      .catch(() => {
        if (!controller.signal.aborted) setFehler('Gerichte konnten nicht geladen werden. Bitte erneut versuchen.');
      })
      .finally(() => { if (!controller.signal.aborted) setLaden(false); });
  }, [suche, filter.kategorie, filter.saison, filter.subkategorie, filter.sortierung, seite]);

  useEffect(() => { setSeite(1); }, [suche, filter.kategorie, filter.saison, filter.subkategorie, filter.sortierung]);
  useEffect(() => {
    ladeGerichte();
    return () => anfrage.current?.abort();
  }, [ladeGerichte]);

  const seiteWechseln = (s: number) => {
    setSeite(s);
    listeOben.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  };

  const filterAktiv = !!(filter.suche || filter.kategorie || filter.saison || filter.subkategorie);

  const neu = () => {
    setFormFehler('');
    setEditId(null);
    setFormular({ name: '', kategorie: filter.kategorie || 'Hauptgang', subkategorie: '', saisonen: filter.saison ? [filter.saison] : [] });
    setFormularOffen(true);
  };

  const bearbeiten = (g: Gericht) => {
    setFormFehler('');
    setEditId(g.id);
    setFormular({
      name: g.name, kategorie: g.kategorie, subkategorie: g.subkategorie || '',
      saisonen: g.saison ? g.saison.split(',').map(s => s.trim()).filter(Boolean) : [],
    });
    setFormularOffen(true);
  };

  const speichern = async () => {
    if (beschaeftigt || !formular.name.trim()) return;
    setBeschaeftigt(true);
    setFormFehler('');
    const daten = {
      name: formular.name.trim(), kategorie: formular.kategorie, subkategorie: formular.subkategorie || null,
      saison: SAISONEN.filter(s => formular.saisonen.includes(s)).join(', '),
    };
    try {
      if (editId === null) await api.post('/gerichte', daten);
      else await api.put(`/gerichte/${editId}`, daten);
      setFormularOffen(false);
      setMeldung(editId === null ? 'Gericht angelegt' : 'Änderungen gespeichert');
      ladeGerichte();
    } catch {
      setFormFehler('Speichern fehlgeschlagen. Ihre Eingaben bleiben erhalten.');
    } finally { setBeschaeftigt(false); }
  };

  const loeschen = async () => {
    if (!loeschKandidat || beschaeftigt) return;
    setBeschaeftigt(true);
    try {
      await api.delete(`/gerichte/${loeschKandidat.id}`);
      setMeldung(`„${loeschKandidat.name}“ gelöscht`);
      setLoeschKandidat(null);
      ladeGerichte();
    } catch {
      setLoeschKandidat(null);
      setFehler('Löschen fehlgeschlagen. Bitte erneut versuchen.');
    } finally { setBeschaeftigt(false); }
  };

  const seitenAnzahl = Math.ceil(gesamt / PRO_SEITE);

  if (duplikateAnsicht) {
    return <Duplikate onZurueck={() => { setDuplikateAnsicht(false); ladeGerichte(); }} />;
  }

  return (
    <Box>
      <Box ref={listeOben} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap', scrollMarginTop: 80 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Gerichte</Typography>
        {darfBearbeiten && <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" startIcon={<ContentCopy />} onClick={() => setDuplikateAnsicht(true)}>
            Duplikate prüfen
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={neu}>Neues Gericht</Button>
        </Box>}
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          label="Suchen" placeholder="z. B. Zander, Kürbis …" value={filter.suche}
          onChange={e => setze({ suche: e.target.value })}
          onKeyDown={e => { if (e.key === 'Escape') setze({ suche: '' }); }}
          sx={{ minWidth: 240, flex: { xs: '1 1 100%', sm: '0 1 280px' } }} size="small"
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
            endAdornment: filter.suche ? (
              <InputAdornment position="end">
                <IconButton size="small" aria-label="Suche leeren" onClick={() => setze({ suche: '' })}><Clear fontSize="small" /></IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />
        <FilterSelect label="Kategorie" value={filter.kategorie} optionen={KATEGORIEN} onChange={v => setze({ kategorie: v })} />
        <FilterSelect label="Art" value={filter.subkategorie} optionen={SUBKATEGORIEN} onChange={v => setze({ subkategorie: v })} />
        <FilterSelect label="Saison" value={filter.saison} optionen={SAISONEN} onChange={v => setze({ saison: v })} />
        <FilterSelect label="Sortierung" value={filter.sortierung} optionen={SORTIERUNGEN} alleLabel={null} minWidth={200}
          onChange={v => setze({ sortierung: v })} />
        {filterAktiv && (
          <Button size="small" startIcon={<FilterAltOff />} onClick={() => setFilter(f => ({ ...LEERE_FILTER, sortierung: f.sortierung }))}>
            Filter zurücksetzen
          </Button>
        )}
      </Box>

      {fehler && <Alert severity="error" sx={{ mb: 2 }} action={<Button onClick={ladeGerichte}>Erneut laden</Button>}>{fehler}</Alert>}

      {ersteLadung && laden ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : (
        <>
          <Box sx={{ height: 4, mb: 1 }}>{laden && <LinearProgress />}</Box>
          <Typography sx={{ color: 'text.secondary', mb: 2 }}>
            {gesamt} Gerichte gefunden{seitenAnzahl > 1 ? ` · Seite ${seite} von ${seitenAnzahl}` : ''}
          </Typography>

          {!laden && gesamt === 0 && !fehler && (
            <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
              <Typography sx={{ mb: 2 }}>Keine Gerichte passen zu Suche und Filtern.</Typography>
              {filterAktiv && (
                <Button variant="outlined" startIcon={<FilterAltOff />} onClick={() => setFilter(f => ({ ...LEERE_FILTER, sortierung: f.sortierung }))}>
                  Filter zurücksetzen
                </Button>
              )}
            </Box>
          )}

          <Grid container spacing={2} sx={{ opacity: laden ? 0.6 : 1, transition: 'opacity 150ms' }}>
            {gerichte.map(g => (
              <Grid item xs={12} sm={6} lg={4} key={g.id}>
                <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column', '&:hover .aktionen': { opacity: 1 } }}>
                  <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1, pb: '12px !important' }}>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      <Chip label={g.kategorie} size="small"
                        sx={{ bgcolor: KATEGORIE_FARBEN[g.kategorie] || '#888', color: 'white', fontWeight: 500 }} />
                      {g.subkategorie && (
                        <Chip label={g.subkategorie} size="small" variant="outlined"
                          sx={{ borderColor: SUBKATEGORIE_FARBEN[g.subkategorie] || '#888', color: SUBKATEGORIE_FARBEN[g.subkategorie] || '#555' }} />
                      )}
                    </Box>
                    <Typography sx={{ fontWeight: 600, lineHeight: 1.35 }}>{g.name}</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto', gap: 0.5 }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                        {g.saison && g.saison.split(',').map(s => s.trim()).filter(Boolean).map(s => (
                          <Box key={s} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.75rem', color: 'text.secondary', mr: 0.5 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: SAISON_FARBEN[s] || '#888' }} />{s}
                          </Box>
                        ))}
                        {g.datum && (
                          <Tooltip title="Erstmals auf der Karte">
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>· {datumFormat(g.datum)}</Typography>
                          </Tooltip>
                        )}
                      </Box>
                      {darfBearbeiten && <Box className="aktionen" sx={{ display: 'flex', flexShrink: 0, opacity: { xs: 1, md: 0.55 }, transition: 'opacity 150ms' }}>
                        <IconButton aria-label={`${g.name} bearbeiten`} disabled={beschaeftigt} size="small" onClick={() => bearbeiten(g)}><Edit fontSize="small" /></IconButton>
                        <IconButton aria-label={`${g.name} löschen`} disabled={beschaeftigt} size="small" color="error" onClick={() => setLoeschKandidat(g)}><Delete fontSize="small" /></IconButton>
                      </Box>}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {seitenAnzahl > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination count={seitenAnzahl} page={seite} onChange={(_, val) => seiteWechseln(val)} color="primary" />
            </Box>
          )}
        </>
      )}

      <Dialog open={formularOffen} onClose={() => { if (!beschaeftigt) setFormularOffen(false); }} maxWidth="sm" fullWidth>
        <Box component="form" onSubmit={(e: React.FormEvent) => { e.preventDefault(); speichern(); }}>
          <DialogTitle>{editId === null ? 'Neues Gericht' : 'Gericht bearbeiten'}</DialogTitle>
          <DialogContent>
            {formFehler && <Alert severity="error" sx={{ mb: 1 }}>{formFehler}</Alert>}
            <TextField
              fullWidth autoFocus label="Name" value={formular.name} multiline maxRows={3}
              onChange={e => setFormular(f => ({ ...f, name: e.target.value }))}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); speichern(); } }}
              inputProps={{ maxLength: 255 }}
              sx={{ mt: 1, mb: 2 }}
            />
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <FormControl fullWidth>
                <InputLabel>Kategorie</InputLabel>
                <Select value={formular.kategorie} label="Kategorie" onChange={e => setFormular(f => ({ ...f, kategorie: e.target.value }))}>
                  {KATEGORIEN.map(k => <MenuItem key={k} value={k}>{k}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Art</InputLabel>
                <Select value={formular.subkategorie} label="Art" onChange={e => setFormular(f => ({ ...f, subkategorie: e.target.value }))}>
                  <MenuItem value="">—</MenuItem>
                  {SUBKATEGORIEN.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
            <FormControl fullWidth>
              <InputLabel>Saison(en)</InputLabel>
              <Select
                multiple
                value={formular.saisonen}
                input={<OutlinedInput label="Saison(en)" />}
                onChange={e => setFormular(f => ({ ...f, saisonen: typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value as string[] }))}
                renderValue={(selected) => SAISONEN.filter(s => (selected as string[]).includes(s)).join(', ')}
              >
                {SAISONEN.map(s => (
                  <MenuItem key={s} value={s}>
                    <Checkbox checked={formular.saisonen.includes(s)} />
                    <ListItemText primary={s} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button disabled={beschaeftigt} onClick={() => setFormularOffen(false)}>Abbrechen</Button>
            <Button type="submit" disabled={beschaeftigt || !formular.name.trim() || !formular.kategorie} variant="contained">
              {editId === null ? 'Anlegen' : 'Speichern'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={!!loeschKandidat} onClose={() => { if (!beschaeftigt) setLoeschKandidat(null); }}>
        <DialogTitle>Gericht löschen?</DialogTitle>
        <DialogContent>
          <DialogContentText>„{loeschKandidat?.name}“ wird endgültig aus der Datenbank entfernt.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button disabled={beschaeftigt} onClick={() => setLoeschKandidat(null)}>Abbrechen</Button>
          <Button disabled={beschaeftigt} color="error" variant="contained" onClick={loeschen}>Löschen</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!meldung} autoHideDuration={3000} onClose={() => setMeldung('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" variant="filled" onClose={() => setMeldung('')}>{meldung}</Alert>
      </Snackbar>
    </Box>
  );
}
